import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { logout } from '../../../app/auth/authSlice';
import axiosInstance, { triggerForceLogout } from '../../../config/axiosApi/axiosApi';
import APIs from '../../../Constants/apis';
import AUTH_CONFIG from '../../../config/authConfig';
import authService from '../../../services/authService';
import {
  getTimeUntilExpiry,
  isTokenExpired,
  isTokenExpiringSoon,
} from '../../../utils/tokenUtils';

/** Backup poll when there is no readable JWT (cookie-only sessions). */
const SESSION_POLL_MS = AUTH_CONFIG.PERIODIC_TOKEN_CHECK || 5 * 60 * 1000;

const TokenExpiryChecker = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const intervalRef = useRef(null);
  const expiryTimerRef = useRef(null);
  const checkInFlightRef = useRef(false);
  // Stable session identity — do not depend on whole `user` (patchUserData would re-poll forever).
  const sessionKey =
    `${user?.userData?.compID ?? user?.userData?.CompID ?? ''}:` +
    `${user?.userData?.userName ?? user?.userData?.UserName ?? ''}`;

  const isOnLoginPage = location.pathname.includes('/user/login');

  const getStoredAccessToken = () => {
    try {
      return sessionStorage.getItem('bo_access_token');
    } catch (_) {
      return null;
    }
  };

  const forceLogoutToLogin = () => {
    authService.clearClientSession();
    dispatch(logout());
    triggerForceLogout();
  };

  const tryRefreshSession = async () => {
    try {
      const result = await authService.refreshToken();
      return !!result?.success;
    } catch (_) {
      return false;
    }
  };

  /**
   * Validate session via profile API. With validateStatus < 500, a 401 may
   * resolve through the interceptor (refresh or force logout) rather than throw.
   */
  const validateSession = async () => {
    try {
      const response = await axiosInstance.get(APIs.GET_COMPANY_BOOTSTRAP_PROFILE);
      if (response?.status === 401) {
        return false;
      }
      return true;
    } catch (error) {
      if (error?.response?.status === 401) {
        return false;
      }
      // Network/timeout: do not log out; retry on next poll.
      return true;
    }
  };

  const scheduleExpiryTimer = (options = {}) => {
    if (expiryTimerRef.current) {
      clearTimeout(expiryTimerRef.current);
      expiryTimerRef.current = null;
    }

    const token = getStoredAccessToken();
    if (!token) {
      return;
    }

    const secondsLeft = getTimeUntilExpiry(token);
    if (secondsLeft < 0) {
      return;
    }

    const refreshThreshold = AUTH_CONFIG.REFRESH_THRESHOLD || 300;
    let delayMs;

    if (options.waitUntilExpiry) {
      // Retry at real expiry after a failed proactive refresh (avoid tight loops).
      delayMs = Math.max(secondsLeft * 1000, 1000);
    } else {
      const msUntilRefreshAttempt = (secondsLeft - refreshThreshold) * 1000;
      // If already inside the refresh window, fire at actual expiry (not in a 30s loop).
      delayMs = msUntilRefreshAttempt > 0
        ? msUntilRefreshAttempt
        : Math.max(secondsLeft * 1000, 1000);
    }

    expiryTimerRef.current = setTimeout(() => {
      checkSession({ reason: 'scheduled-expiry' });
    }, delayMs);
  };

  const checkSession = async ({ forceApiCheck = false } = {}) => {
    if (checkInFlightRef.current || isOnLoginPage || !isAuthenticated || !user) {
      return;
    }

    checkInFlightRef.current = true;
    try {
      const token = getStoredAccessToken();

      if (token) {
        const expired = isTokenExpired(token);
        const expiringSoon = isTokenExpiringSoon(token, AUTH_CONFIG.REFRESH_THRESHOLD || 300);

        if (expired || expiringSoon) {
          const refreshed = await tryRefreshSession();
          if (!refreshed) {
            // Only force logout once the access token is actually dead.
            // A transient refresh failure while still within JWT lifetime should retry.
            if (expired || isTokenExpired(getStoredAccessToken() || token)) {
              forceLogoutToLogin();
            } else {
              scheduleExpiryTimer({ waitUntilExpiry: true });
            }
            return;
          }
          scheduleExpiryTimer();
          return;
        }

        scheduleExpiryTimer();

        // Readable JWT still valid — only hit the API when forced (visibility/mount/poll backup).
        if (!forceApiCheck) {
          return;
        }
      }

      const stillValid = await validateSession();
      if (!stillValid) {
        forceLogoutToLogin();
      } else {
        scheduleExpiryTimer();
      }
    } finally {
      checkInFlightRef.current = false;
    }
  };

  // Mount + periodic backup poll
  useEffect(() => {
    if (isOnLoginPage || !isAuthenticated || !sessionKey || sessionKey === ':') {
      return undefined;
    }

    checkSession({ forceApiCheck: true });
    intervalRef.current = setInterval(
      () => checkSession({ forceApiCheck: true }),
      SESSION_POLL_MS
    );

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (expiryTimerRef.current) {
        clearTimeout(expiryTimerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sessionKey replaces unstable `user` ref
  }, [sessionKey, isAuthenticated, isOnLoginPage]);

  // Other-tab logout sync
  useEffect(() => {
    if (isOnLoginPage || !isAuthenticated || !sessionKey || sessionKey === ':') {
      return undefined;
    }

    const handleStorageChange = (e) => {
      if (e.key === 'user') {
        if (!e.newValue) {
          forceLogoutToLogin();
        } else {
          checkSession({ forceApiCheck: true });
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnLoginPage, isAuthenticated, sessionKey]);

  // Re-check when tab becomes visible
  useEffect(() => {
    if (isOnLoginPage || !isAuthenticated || !sessionKey || sessionKey === ':') {
      return undefined;
    }

    const handleVisibilityChange = () => {
      if (!document.hidden && user?.userData) {
        checkSession({ forceApiCheck: true });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionKey, isOnLoginPage, isAuthenticated]);

  // Clear Redux when axios forces logout (storage already cleared in triggerForceLogout)
  useEffect(() => {
    const handleForceLogout = () => {
      dispatch(logout());
    };

    window.addEventListener('forceLogout', handleForceLogout);
    return () => window.removeEventListener('forceLogout', handleForceLogout);
  }, [dispatch]);

  return null;
};

export default TokenExpiryChecker;
